import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  XMarkIcon, 
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  TruckIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { payslipsService } from '../../../services/accounting/payslips';
import { formatCurrency, formatDate } from '../../../utils/formatting';
import LoadingSpinner from '../../ui/LoadingSpinner';
import toast from 'react-hot-toast';

/**
 * PaymentHistoryModal Component
 * 
 * Modal for displaying employee payment history
 */
const PaymentHistoryModal = ({ isOpen, onClose, employee }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [filter, setFilter] = useState('all'); // all, payroll, commission
  const [sortBy, setSortBy] = useState('date'); // date, amount, type

  // Load payment history when modal opens
  useEffect(() => {
    if (isOpen && employee && currentCompany?.id) {
      loadPaymentHistory();
    }
  }, [isOpen, employee, currentCompany?.id]);

  const loadPaymentHistory = async () => {
    try {
      setLoading(true);
      const history = await payslipsService.getEmployeePaymentHistory(currentCompany.id, employee.id);
      setPaymentHistory(history);
    } catch (error) {
      console.error('Error loading payment history:', error);
      toast.error(t('payroll.error.loading_history', 'Failed to load payment history'));
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort payment history
  const filteredHistory = paymentHistory
    .filter(payment => {
      if (filter === 'all') return true;
      return payment.type === filter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'amount':
          return b.amount - a.amount;
        case 'type':
          return a.type.localeCompare(b.type);
        case 'date':
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

  // Calculate totals
  const totals = paymentHistory.reduce((acc, payment) => {
    acc.total += payment.amount || 0;
    if (payment.type === 'payroll') {
      acc.payroll += payment.amount || 0;
    } else if (payment.type === 'commission') {
      acc.commission += payment.amount || 0;
    }
    return acc;
  }, { total: 0, payroll: 0, commission: 0 });

  const getPaymentIcon = (payment) => {
    switch (payment.type) {
      case 'payroll':
        return <UserIcon className="w-5 h-5 text-blue-600" />;
      case 'commission':
        return <TruckIcon className="w-5 h-5 text-purple-600" />;
      default:
        return <CurrencyDollarIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getPaymentStatusIcon = (payment) => {
    if (payment.paymentStatus === 'paid' || payment.isPaid) {
      return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
    }
    return <ExclamationCircleIcon className="w-4 h-4 text-amber-500" />;
  };

  const handleClose = () => {
    setPaymentHistory([]);
    setFilter('all');
    setSortBy('date');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <ClockIcon className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('payroll.payment_history', 'Payment History')}
              </h2>
              <p className="text-sm text-gray-500">
                {employee?.name} - {t('payroll.all_payments', 'All Payments')}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(totals.total)}</div>
              <div className="text-sm text-blue-700">{t('payroll.total_payments', 'Total Payments')}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{formatCurrency(totals.payroll)}</div>
              <div className="text-sm text-green-700">{t('payroll.payroll_payments', 'Payroll Payments')}</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{formatCurrency(totals.commission)}</div>
              <div className="text-sm text-purple-700">{t('payroll.commission_payments', 'Commission Payments')}</div>
            </div>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.filter', t('common.filter'))}
                </label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="form-select text-sm"
                >
                  <option value="all">{t('common.all', 'All')}</option>
                  <option value="payroll">{t('payroll.payroll', 'Payroll')}</option>
                  <option value="commission">{t('payroll.commission', 'Commission')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.sort_by', 'Sort By')}
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-select text-sm"
                >
                  <option value="date">{t('common.date', t('common.date'))}</option>
                  <option value="amount">{t('common.amount', t('common.amount'))}</option>
                  <option value="type">{t('common.type', 'Type')}</option>
                </select>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {filteredHistory.length} {t('payroll.payments_found', 'payments found')}
            </div>
          </div>
        </div>

        {/* Payment History List */}
        <div className="overflow-y-auto max-h-[calc(90vh-400px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3">{t('common.loading', t('common.loading'))}</span>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t('payroll.no_payment_history', 'No payment history found')}</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredHistory.map((payment, index) => (
                <div key={`${payment.type}-${payment.id}-${index}`} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {getPaymentIcon(payment)}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-gray-900">
                            {payment.type === 'payroll' 
                              ? `${t('payroll.payroll', 'Payroll')} - ${payment.periodYear}-${String(payment.periodMonth).padStart(2, '0')}`
                              : `${t('payroll.commission', 'Commission')} - ${payment.trackingNumber || 'N/A'}`
                            }
                          </h4>
                          {getPaymentStatusIcon(payment)}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {formatDate(payment.date)}
                          {payment.type === 'commission' && payment.origin && payment.destination && (
                            <span className="ml-2">
                              • {payment.origin} → {payment.destination}
                            </span>
                          )}
                        </div>
                        {payment.type === 'payroll' && (
                          <div className="text-xs text-gray-400 mt-1">
                            {t('payroll.basic_salary', 'Basic Salary')}: {formatCurrency(payment.basicSalary || 0)}
                            {payment.commission > 0 && (
                              <span className="ml-2">
                                • {t('payroll.commission', 'Commission')}: {formatCurrency(payment.commission)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900">
                        {formatCurrency(payment.amount || payment.netPay || 0)}
                      </div>
                      <div className={`text-xs font-medium ${
                        payment.paymentStatus === 'paid' || payment.isPaid
                          ? 'text-green-600'
                          : 'text-amber-600'
                      }`}>
                        {payment.paymentStatus === 'paid' || payment.isPaid
                          ? t('payroll.paid', t('common.paid'))
                          : t('payroll.pending', t('common.pending'))
                        }
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="btn btn-secondary"
          >
            {t('common.close', t('common.close'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;